// The categorySplit value grammar shared by the rule cell and the part sub-rows:
// "CAT:N%" a proportion of the pooled amount, "CAT:N" the literal amount per unit,
// "CAT:*" whatever is left. Parts are comma-separated on the rule's value.
class OperaRuleSplit
{
    static parts(value: string): {cat: string, raw: string}[] {
        let parts: {cat: string, raw: string}[] = [];
        let text = (value || "").trim();
        if (text.length == 0) return parts;
        for (let part of text.split(",")) {
            let kv = part.split(":");
            parts.push({cat: (kv[0] || "").trim(), raw: (kv[1] || "").trim()});
        }
        return parts;
    }

    static serialize(parts: {cat: string, raw: string}[]): string {
        return parts.map(p => p.cat + ":" + p.raw).join(",");
    }

    // Categories must be declared; a pure-percent split (no *) must sum to 100, with
    // a * the percentages must not exceed 100, and only one * makes sense.
    static validate(text: string, options: string[]): string {
        let value = (text || "").trim();
        if (value.length == 0) return MIOLocalizeString("SPLIT_EMPTY", "empty split");

        let pctSum = 0;
        let allPercent = true;
        let remainders = 0;
        for (let part of value.split(",")) {
            let kv = part.split(":");
            let cat = (kv[0] || "").trim();
            let raw = (kv[1] || "").trim();
            let isRemainder = raw == "*";
            let isPercent = !isRemainder && raw.length > 0 && raw.charAt(raw.length - 1) == "%";
            let n = isRemainder ? 0 : parseFloat(isPercent ? raw.substring(0, raw.length - 1).trim() : raw);
            if (kv.length != 2 || cat.length == 0 || isNaN(n)) {
                return MIOLocalizeString("SPLIT_MALFORMED", "expected CATEGORY:NUMBER (literal per unit), CATEGORY:NUMBER% (proportion) or CATEGORY:* (remainder)") + ": " + part.trim();
            }
            if (options.length > 0 && options.indexOf(cat) < 0) {
                return "“" + cat + "” " + MIOLocalizeString("NOT_A_CATEGORY", "is not a declared category");
            }
            if (isRemainder) { remainders++; allPercent = false; }
            else if (isPercent) pctSum += n;
            else allPercent = false;
        }
        if (remainders > 1) return MIOLocalizeString("SPLIT_MULTIPLE_REMAINDERS", "only one * remainder makes sense");
        if (remainders > 0 && pctSum > 100) return MIOLocalizeString("SPLIT_REMAINDER_NEGATIVE", "percentages exceed 100 — the * remainder goes negative");
        if (allPercent && remainders == 0 && pctSum != 100) return MIOLocalizeString("SPLIT_SUM", "percentages sum to") + " " + pctSum + ", " + MIOLocalizeString("NOT_100", "not 100");
        return null;
    }
}

// Indented sub-row under a multiple-results rule: one part of the split — the
// categorization picked from the vocabulary and its value (20% | 20 | *). Edits
// write straight into the owning rule's value string; the standard delete
// accessory removes the part (handled by the VC).
class OperaMappingRuleSplitPartCell extends UITableViewCell
{
    private catComboBox: MUIComboBox = null;
    private valueTextField: MUITextField = null;

    // Declared categories, set by the VC before the part.
    valueOptions: string[] = [];

    awakeFromHTML() {
        this.catComboBox = MUIOutlet(this, "cat-cb", "MUIComboBox");
        this.catComboBox.setOnChangeAction(this, function(this:OperaMappingRuleSplitPartCell, cb: MUIComboBox, value: string) {
            let parts = OperaRuleSplit.parts(this._rule.value);
            if (parts[this._partIndex] == null) return;
            parts[this._partIndex].cat = value;
            this._rule.value = OperaRuleSplit.serialize(parts);
            this.validate();
        });

        this.valueTextField = MUIOutlet(this, "value-tf", "MUITextField");
        this.valueTextField.setOnChangeText(this, function(this:OperaMappingRuleSplitPartCell, tf: MUITextField, value: string) {
            let parts = OperaRuleSplit.parts(this._rule.value);
            if (parts[this._partIndex] == null) return;
            parts[this._partIndex].raw = value.trim();
            this._rule.value = OperaRuleSplit.serialize(parts);
            this.validate();
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _rule: IntegratorMappingRule = null;
    private _partIndex = -1;
    setPart(rule: IntegratorMappingRule, partIndex: number) {
        this._rule = rule;
        this._partIndex = partIndex;

        let parts = OperaRuleSplit.parts(rule.value);
        let part = parts[partIndex] != null ? parts[partIndex] : {cat: "", raw: ""};

        this.catComboBox.removeAllItems();
        for (let name of this.valueOptions) this.catComboBox.addItem(name, name);
        if (part.cat.length > 0 && this.valueOptions.indexOf(part.cat) < 0) this.catComboBox.addItem(part.cat, part.cat);
        this.catComboBox.selectItem(part.cat);

        this.valueTextField.text = part.raw;
        this.validate();
    }

    private validate() {
        let error = OperaRuleSplit.validate(this._rule.value, this.valueOptions);
        let layer = this.valueTextField.layer;
        if (error != null) { layer.classList.add("error"); layer.title = error; }
        else { layer.classList.remove("error"); layer.title = ""; }
    }
}
